import React, { useEffect, useState } from 'react';

import { washHub } from 'store/slices';
import { useWashHubListener } from 'hooks';

import { useQuery } from 'react-query';
import { fetchScans } from 'api/items';

import useQueryParams from 'hooks/useQueryParams';
import useUrlSharing from 'hooks/useUrlSharing';
import { ScansProps } from './Scans.props';

import ScansView from './Scans.view';

const ScansContainer = (_: ScansProps) => {
  const [page, setPage] = useState<number>(1);
  const [sku, setSku] = useState<string[]>();
  const [site, setSite] = useState<string[]>();
  const [status, setStatus] = useState<number[]>();
  const [endDate, setEndDate] = useState<Date>();
  const [startDate, setStartDate] = useState<Date>();
  const [isUrlMapped, setIsUrlMapped] = useState<boolean>(false);
  const [type, setType] = useState<string>();
  const [urlRecord, setUrlRecord] = useState({});
  const queryUrl = useQueryParams();

  const washHubId = washHub.selectors.useSelectedWashHubId();
  const isWashHubIdValid = washHub.selectors.useIsWashHubIdValid();
  const isUpdated = useUrlSharing(urlRecord, washHubId);

  const queryKey = [
    `fetch-all-scan-${washHubId}`,
    page,
    status,
    sku,
    site,
    endDate,
    startDate,
  ];

  const queryFn = () =>
    fetchScans({
      sku,
      page,
      site,
      status,
      limit: 10,
      washHub: washHubId,
      endDate,
      startDate,
    });

  const options = {
    keepPreviousData: true,
    refetchOnMount: 'always' as any,
    enabled: isWashHubIdValid,
  };

  const { data, isFetching, isLoading } = useQuery(queryKey, queryFn, options);

  useWashHubListener(() => {
    setPage(1);
    setStatus(undefined);

    setSku(undefined);
    setSite(undefined);
    setStartDate(undefined);
    setEndDate(undefined);
  });

  const scans = data?.items || [];
  const totalCount = data?.total || 0;
  const totalPages = data?.totalPages || 0;

  const pagination = {
    page,
    setPage,
    totalCount,
    totalPages,
  };

  useEffect(() => {
    const urlParams = {
      washhubId: String(washHubId),
      ...(type ? { type } : {}),
      ...(site && site.length ? { site: site.join('-') } : {}),
      ...(status && status.length ? { status: status.join('-') } : {}),
      ...(sku && sku.length ? { sku: sku.join('-') } : {}),
      ...(startDate ? { startDate: `${startDate.valueOf()}` } : {}),
      ...(endDate ? { endDate: `${endDate.valueOf()}` } : {}),
    };
    setUrlRecord(urlParams);
  }, [washHubId, status, site, sku, type, endDate, startDate]);

  useEffect(() => {
    const { status, site, sku, type, startDate, endDate } = queryUrl;
    status && setStatus(status.split('-').map((el) => +el));
    site && setSite(site.split('-'));
    sku && setSku(sku.split('-'));
    type && setType(type);
    startDate && setStartDate(new Date(+startDate));
    endDate && setEndDate(new Date(+endDate));
    if (isUpdated) setIsUrlMapped(true);
  }, [isUpdated]);

  const onApplyFilters = (data: string[], type: string) => {
    setPage(1);
    setType(type);
    if (type === 'sku') setSku(data);
    if (type === 'site') setSite(data);
  };

  const onClearFilters = () => {
    setPage(1);

    setSku(undefined);
    setSite(undefined);
  };

  const onSearchPress = (startDate: Date | null, endDate: Date | null) => {
    setPage(1);

    if (startDate) {
      setStartDate(startDate);
    }

    if (endDate) {
      setEndDate(endDate);
    }
  };

  const onSearchClear = () => {
    setPage(1);
    setStartDate(undefined);
    setEndDate(undefined);
  };

  const _props = {
    scans,
    isLoading: isLoading || isFetching,
    pagination,
    status,
    setStatus,
    onApplyFilters,
    onClearFilters,
    sku,
    site,
    venue: type ? (type == 'sku' ? sku : site) : [],
    onSearchPress,
    onSearchClear,
    startDate,
    endDate,
  };

  if (!isUrlMapped) return null;

  return <ScansView {..._props} />;
};

export default ScansContainer;
